import { StyleSheet, View } from '@react-pdf/renderer';
import type { Style } from '@react-pdf/types';
import { usePdfxTheme, useSafeMemo } from '../../../lib/pdfx-theme-context';
import type React from 'react';
type PdfxTheme = ReturnType<typeof usePdfxTheme>;

export type SectionSpacing = 'none' | 'sm' | 'md' | 'lg' | 'xl';
export type SectionPadding = 'none' | 'sm' | 'md' | 'lg';
export type SectionVariant = 'default' | 'callout' | 'highlight' | 'card';

/**
 * Layout container with spacing, padding, and visual style variants.
 * Props - `spacing` | `padding` | `background` | `border` | `variant` | `accentColor` | `noWrap` | `children` | `style`
 * @see {@link SectionProps}
 */
export interface SectionProps {
  /** Custom styles to merge with component defaults */
  style?: Style;
  /** Content to render */
  children: React.ReactNode;
  /**
   * @default 'md'
   */
  spacing?: SectionSpacing;
  padding?: SectionPadding;
  background?: string;
  /**
   * @default false
   */
  border?: boolean;
  /**
   * @default 'default'
   */
  variant?: SectionVariant;
  accentColor?: string;
  /**
   * @default false
   */
  noWrap?: boolean;
}

const THEME_COLOR_KEYS = ['foreground','muted','mutedForeground','primary','primaryForeground','accent','destructive','success','warning','info'] as const;
function resolveColor(value: string, colors: Record<string, string>): string {
  return THEME_COLOR_KEYS.includes(value as (typeof THEME_COLOR_KEYS)[number]) ? colors[value] : value;
}
function createSectionStyles(t: PdfxTheme) {
  const { spacing, borderRadius } = t.primitives;
  return StyleSheet.create({
    base: { flexDirection: 'column' },
    spacingNone: { marginVertical: spacing[0] },
    spacingSm: { marginVertical: spacing[4] },
    spacingMd: { marginVertical: t.spacing.sectionGap },
    spacingLg: { marginVertical: spacing[8] },
    spacingXl: { marginVertical: spacing[12] },
    paddingNone: { padding: spacing[0] },
    paddingSm: { padding: spacing[3] },
    paddingMd: { padding: spacing[4] },
    paddingLg: { padding: spacing[6] },
    border: {
      borderWidth: spacing[0.5],
      borderColor: t.colors.border,
      borderStyle: 'solid',
      borderRadius: borderRadius.md,
    },
    callout: {
      borderLeftWidth: spacing[1],
      borderLeftColor: t.colors.primary,
      borderLeftStyle: 'solid',
      paddingLeft: spacing[4],
      paddingVertical: spacing[2],
    },
    highlight: {
      backgroundColor: t.colors.muted,
      borderLeftWidth: spacing[1],
      borderLeftColor: t.colors.primary,
      borderLeftStyle: 'solid',
      padding: spacing[4],
    },
    card: {
      borderWidth: spacing[0.5],
      borderColor: t.colors.border,
      borderStyle: 'solid',
      borderRadius: borderRadius.md,
      padding: spacing[4],
    },
  });
}

export function Section({
  spacing = 'md',
  padding,
  background,
  border,
  variant = 'default',
  accentColor,
  noWrap = false,
  children,
  style,
}: SectionProps) {
  const theme = usePdfxTheme();
  const styles = useSafeMemo(() => createSectionStyles(theme), [theme]);
  const spacingMap = {
    none: styles.spacingNone,
    sm: styles.spacingSm,
    md: styles.spacingMd,
    lg: styles.spacingLg,
    xl: styles.spacingXl,
  };
  const paddingMap = {
    none: styles.paddingNone,
    sm: styles.paddingSm,
    md: styles.paddingMd,
    lg: styles.paddingLg,
  };
  const variantMap: Record<SectionVariant, Style | null> = {
    default: null,
    callout: styles.callout,
    highlight: styles.highlight,
    card: styles.card,
  };
  const styleArray: Style[] = [styles.base, spacingMap[spacing]];
  const variantStyle = variantMap[variant];
  if (variantStyle) styleArray.push(variantStyle);
  if (accentColor && (variant === 'callout' || variant === 'highlight')) {
    styleArray.push({ borderLeftColor: resolveColor(accentColor, theme.colors) });
  }
  if (padding && padding in paddingMap) styleArray.push(paddingMap[padding]);
  if (border && variant === 'default') styleArray.push(styles.border);
  if (background) styleArray.push({ backgroundColor: resolveColor(background, theme.colors) });
  if (style) styleArray.push(...[style].flat());
  return (
    <View wrap={noWrap ? false : undefined} style={styleArray}>
      {children}
    </View>
  );
}
